import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { groupBy, isEmpty, isNil } from 'lodash';
import { RuleType } from '../../IRule.ts';
import { type IRuleType } from '../../IRuleType.ts';
import { UniqueFieldRuleComponent } from './UniqueFieldRuleComponent.tsx';

const SingleUniqueFieldSchema = yup.object({
  fieldCodes: yup
    .array(yup.string().required())
    .min(1)
    .required()
    .label('Codici dei campi'),
  customMessage: yup
    .string()
    .notRequired()
    .label('Messaggio personalizzato di validazione'),
});

export type IUniqueSingleFieldRule = yup.InferType<
  typeof SingleUniqueFieldSchema
>;

const UniqueFieldRuleSchema = yup
  .object({
    groups: yup
      .array(SingleUniqueFieldSchema)
      .notRequired()
      .label('Gruppi di campi unici'),
  })
  .notRequired()
  .noUnknown()
  .transform((value: any) => {
    // Se è presente il formato legacy con "fields", lo trasforma nel nuovo formato
    if (value?.fields) {
      return {
        groups: value.fields.map((item: { field: string }) => ({
          fieldCodes: [item.field],
        })),
      };
    }
    return value;
  });

export type IUniqueFieldRuleType = yup.InferType<typeof UniqueFieldRuleSchema>;

/**
 * Funzione che permette di ottenere la label di un campo di una lista ripetibile.
 */
function getInnerSchemaLabel(
  arraySchema: any,
  fieldName: string,
): string | undefined {
  return arraySchema.innerType?.fields?.[fieldName]?.spec?.label;
}

/**
 * Crea un test di validazione per l'unicità di una combinazione di campi
 */
function createUniqueFieldGroupTest(
  fieldCodes: string[],
  customMessage: string | undefined | null,
  schema: any,
) {
  return schema.test({
    name: 'unique-field-group',
    exclusive: false,
    skipAbsent: true,
    message:
      customMessage ??
      t(
        'rules.unique-field-rule.error',
        'La combinazione dei campi {{labels}} deve essere univoca',
        {
          labels: fieldCodes
            .map(code => getInnerSchemaLabel(schema, code) ?? code)
            .join(', '),
        },
      ),
    test(records: Array<Record<string, any>>) {
      if (records == null) return true;

      // Esclude i record dove tutti i valori della combinazione sono null/undefined
      const validRecords = records.filter(record => {
        const fieldValues = fieldCodes.map(code => record[code]);
        return !fieldValues.every(isNil);
      });

      // Raggruppa i record per combinazione di valori
      const groups = groupBy(validRecords, record => {
        const fieldValues = fieldCodes.map(code => record[code]);
        return JSON.stringify(fieldValues);
      });

      // Controlla se ci sono gruppi con più di un elemento (duplicati)
      const hasDuplicates = Object.values(groups).some(
        group => group.length > 1,
      );

      return !hasDuplicates;
    },
  });
}

/**
 * Regola che permette di validare una combinazione di campi di una lista ripetibile
 * come "univoca". In questo modo, non è possibile impostare la stessa
 * combinazione di valori per più elementi della lista.
 */
export const UniqueFieldRule: IRuleType<IUniqueFieldRuleType> = {
  type: RuleType.UniqueField,
  name: 'Campi unici',
  configComponent: UniqueFieldRuleComponent,
  configValidator: UniqueFieldRuleSchema,
  validator: (schema, rawRule) => {
    // Facciamo il cast per gestire il legacy in compilazione
    const rule = UniqueFieldRuleSchema.cast(rawRule, { assert: false });
    if (!rule?.groups) return schema;

    let resultSchema = schema;
    for (const fieldGroup of rule.groups) {
      if (
        !Array.isArray(fieldGroup.fieldCodes) ||
        isEmpty(fieldGroup.fieldCodes)
      ) {
        continue;
      }

      resultSchema = createUniqueFieldGroupTest(
        fieldGroup.fieldCodes,
        fieldGroup.customMessage,
        resultSchema,
      );
    }

    return resultSchema;
  },
};
